import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import * as React from 'react';
import { cn } from '../../../lib/utils';
import { SpecraButton } from '../ensigns/button';
import { SpecraText } from '../ensigns/typography';
import { chartToneVar, SpecraChartFrame, SpecraChartLegend, SpecraChartLink } from '../lieutenants/chart-frame';
import { SpecraTooltip, SpecraTooltipProvider } from '../lieutenants/tooltip';
const VALUE_TONE = {
    danger: 'danger',
    warning: 'warning',
    muted: 'muted'
};
const totalOf = (r, segments)=>segments.reduce((n, s)=>n + (r.values[s.key] ?? 0), 0);
/** Groups as stacked bars on one scale, each segment a link to its rows. */ export function SpecraStackedBarList({ segments, rows, folded = [], foldedLabel = (n)=>`+ ${n} more`, valueLabel, emptyMessage, renderLink, animate = false, className, getRowTestId, getSegmentTestId, title, description, actions, insight, 'data-testid': testId }) {
    const [open, setOpen] = React.useState(false);
    const scale = Math.max(1, ...[
        ...rows,
        ...folded
    ].map((r)=>totalOf(r, segments)));
    const empty = rows.length + folded.length === 0;
    // With nothing worth flagging, the quiet rows are the chart: never fold them all away.
    const foldable = rows.length > 0 && folded.length > 0;
    const shown = open || !foldable ? [
        ...rows,
        ...folded
    ] : rows;
    const table = {
        columns: [
            {
                key: 'group',
                label: 'Group'
            },
            ...segments.map((s)=>({
                    key: s.key,
                    label: s.label,
                    numeric: true
                })),
            {
                key: 'value',
                label: valueLabel,
                numeric: true
            }
        ],
        rows: [
            ...rows,
            ...folded
        ].map((r)=>({
                id: r.id,
                cells: {
                    group: r.sublabel ? `${r.label} · ${r.sublabel}` : r.label,
                    value: r.value,
                    ...Object.fromEntries(segments.map((s)=>[
                            s.key,
                            (r.values[s.key] ?? 0).toLocaleString()
                        ]))
                }
            }))
    };
    return /*#__PURE__*/ _jsx(SpecraChartFrame, {
        actions: actions,
        className: className,
        "data-testid": testId,
        description: description,
        insight: empty ? undefined : insight,
        legend: empty ? undefined : /*#__PURE__*/ _jsx(SpecraChartLegend, {
            items: segments.map((s)=>({
                    label: s.label,
                    tone: s.tone
                }))
        }),
        table: table,
        title: title,
        children: empty ? /*#__PURE__*/ _jsxs("div", {
            className: "relative space-y-[var(--space-3)] py-[var(--space-2)]",
            "data-chart-empty": "",
            children: [
                [
                    0.7,
                    0.5,
                    0.35
                ].map((w)=>/*#__PURE__*/ _jsxs("div", {
                        className: "flex items-center gap-[var(--space-3)]",
                        children: [
                            /*#__PURE__*/ _jsx("span", {
                                className: "h-2 w-[var(--component-chart-label-width)] shrink-0 rounded-[var(--radius-xs)] bg-[var(--chart-heat-0)]"
                            }),
                            /*#__PURE__*/ _jsx("span", {
                                className: "h-[var(--component-chart-bar-height)] rounded-[var(--radius-xs)] bg-[var(--chart-heat-0)]",
                                style: {
                                    width: `${w * 100}%`
                                }
                            })
                        ]
                    }, w)),
                /*#__PURE__*/ _jsx("div", {
                    className: "absolute inset-0 flex items-center justify-center",
                    children: /*#__PURE__*/ _jsx(SpecraText, {
                        as: "p",
                        className: "max-w-sm rounded-[var(--radius-md)] bg-[var(--surface-card)] px-[var(--space-2)] py-[var(--space-1)] text-center",
                        size: "body-ui",
                        tone: "muted",
                        children: emptyMessage
                    })
                })
            ]
        }) : /*#__PURE__*/ _jsxs(SpecraTooltipProvider, {
            delayDuration: 100,
            children: [
                /*#__PURE__*/ _jsx("ul", {
                    "aria-label": title,
                    className: "space-y-[var(--space-2)]",
                    children: shown.map((r)=>/*#__PURE__*/ _jsx(BarRow, {
                            animate: animate,
                            getSegmentTestId: getSegmentTestId,
                            renderLink: renderLink,
                            row: r,
                            scale: scale,
                            segments: segments,
                            testId: getRowTestId?.(r)
                        }, r.id))
                }),
                foldable ? /*#__PURE__*/ _jsx(SpecraButton, {
                    "aria-expanded": open,
                    className: "mt-[var(--space-2)] self-start text-[length:var(--fs-ui-body)]",
                    "data-chart-fold": "",
                    onClick: ()=>setOpen((v)=>!v),
                    size: "inline",
                    variant: "link",
                    children: open ? 'Show fewer' : foldedLabel(folded.length)
                }) : null
            ]
        })
    });
}
function BarRow({ row, segments, scale, renderLink, animate, testId, getSegmentTestId }) {
    const total = totalOf(row, segments);
    const drawn = segments.filter((s)=>(row.values[s.key] ?? 0) > 0);
    return /*#__PURE__*/ _jsxs("li", {
        className: "flex items-center gap-[var(--space-3)]",
        "data-item-id": row.id,
        "data-testid": testId,
        children: [
            /*#__PURE__*/ _jsxs("div", {
                className: "w-[var(--component-chart-label-width)] min-w-0 shrink-0",
                children: [
                    /*#__PURE__*/ _jsx(SpecraText, {
                        as: "div",
                        className: "truncate",
                        size: "body-ui",
                        title: row.label,
                        children: row.label
                    }),
                    row.sublabel ? /*#__PURE__*/ _jsx(SpecraText, {
                        as: "div",
                        className: "truncate",
                        size: "micro",
                        tone: "muted",
                        title: row.sublabel,
                        children: row.sublabel
                    }) : null
                ]
            }),
            /*#__PURE__*/ _jsxs("div", {
                className: "flex min-w-0 flex-1 items-center gap-[var(--space-2)]",
                children: [
                    /*#__PURE__*/ _jsx("div", {
                        className: cn('flex h-[var(--component-chart-bar-height)] min-w-0 gap-0.5', animate && 'specra-bar-grow'),
                        style: {
                            width: `${total / scale * 100}%`
                        },
                        children: drawn.map((s, k)=>{
                            const n = row.values[s.key] ?? 0;
                            const faded = row.faded?.includes(s.key);
                            return /*#__PURE__*/ _jsx(SpecraTooltip, {
                                content: /*#__PURE__*/ _jsxs("span", {
                                    className: "block",
                                    children: [
                                        /*#__PURE__*/ _jsxs("span", {
                                            className: "block font-semibold",
                                            children: [
                                                n.toLocaleString(),
                                                " ",
                                                s.label.toLowerCase(),
                                                " of ",
                                                total.toLocaleString()
                                            ]
                                        }),
                                        /*#__PURE__*/ _jsxs("span", {
                                            className: "block",
                                            children: [
                                                row.label,
                                                row.sublabel ? ` · ${row.sublabel}` : ''
                                            ]
                                        }),
                                        row.hint ? /*#__PURE__*/ _jsx("span", {
                                            className: "block",
                                            children: row.hint
                                        }) : null
                                    ]
                                }),
                                delayDuration: 100,
                                children: /*#__PURE__*/ _jsx(SpecraChartLink, {
                                    "aria-label": `${row.label}: ${n.toLocaleString()} ${s.label.toLowerCase()} of ${total.toLocaleString()}`,
                                    className: cn('h-full min-w-[var(--space-0-5)] transition-opacity hover:opacity-80', k === drawn.length - 1 && 'rounded-r-[var(--radius-xs)]', faded && 'opacity-30'),
                                    "data-item-id": s.key,
                                    "data-testid": getSegmentTestId?.(row, s),
                                    href: row.hrefs?.[s.key],
                                    renderLink: renderLink,
                                    style: {
                                        flexGrow: n,
                                        flexBasis: 0,
                                        background: chartToneVar[s.tone]
                                    }
                                })
                            }, s.key);
                        })
                    }),
                    /*#__PURE__*/ _jsx(SpecraText, {
                        className: "shrink-0 font-semibold tabular-nums",
                        size: "meta",
                        tone: VALUE_TONE[row.valueTone ?? 'muted'],
                        children: row.value
                    })
                ]
            })
        ]
    });
}
